import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_SHIP } from '../graphql/queries';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './ShipDetail.css';

const ShipDetail = () => {
  const { shipId } = useParams();
  const navigate = useNavigate();
  const [currentCursor, setCurrentCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);

  const { loading, error, data, refetch } = useQuery(GET_SHIP, {
    variables: {
      id: shipId,
      first: 20,
      after: currentCursor
    }
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error} />;
  if (!data?.getShip) {
    return (
      <div className="ship-detail">
        <div className="ship-detail-header">
          <button
            className="back-button"
            onClick={() => navigate('/')}
          >
            ← 一覧に戻る
          </button>
          <h1>船舶詳細情報</h1>
        </div>
        <ErrorMessage error={{ message: '船舶が見つかりません' }} />
      </div>
    );
  }

  const ship = data.getShip;

  const formatDate = (dateString) => {
    if (!dateString) return '未定';
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDirectionText = (direction) => {
    if (!direction) return '不明';
    switch (direction) {
      case 'north':
        return '北航船';
      case 'south':
        return '南航船';
      default:
        return '不明';
    }
  };

  const getDestinationLabel = (direction) => {
    if (!direction) return '入港/出港';
    switch (direction) {
      case 'north':
        return '入港';
      case 'south':
        return '出港';
      default:
        return '入港/出港';
    }
  };

  const getShipTypeIcon = (kind) => {
    const defaultIcon = '/static/default-ship-icon.png';

    // アイコンURLが提供されている場合はそれを使用
    if (kind?.iconUrl) {
      return (
        <img
          src={kind.iconUrl}
          alt={kind.name || '船種'}
          className="ship-type-icon"
          onError={(e) => {
            // 画像の読み込みに失敗した場合はデフォルト画像を表示
            e.target.src = defaultIcon;
          }}
        />
      );
    }

    // iconUrlがない場合はデフォルト画像を表示
    return (
      <img
        src={defaultIcon}
        alt={kind?.name || '船種'}
        className="ship-type-icon"
      />
    );
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'fresh':
        return { text: '新規', color: '#4CAF50', icon: '✨' };
      case 'alive':
        return { text: '有効', color: '#2196F3', icon: '✅' };
      case 'updated':
        return { text: '変更あり', color: '#FF9800', icon: '🔄' };
      case 'ongoing':
        return { text: '進行中', color: '#9C27B0', icon: '🚀' };
      case 'expired':
        return { text: '期限切れ', color: '#F44336', icon: '⏰' };
      case 'deleted':
        return { text: '削除済み', color: '#757575', icon: '🗑️' };
      case 'ambiguous':
        return { text: '不明確', color: '#FF5722', icon: '❓' };
      case 'unknown':
      default:
        return { text: '不明', color: '#9E9E9E', icon: '❓' };
    }
  };

  const handlePassagePlanClick = (passagePlanId) => {
    navigate(`/passage-plan/${passagePlanId}`);
  };

  const handleNextPage = async () => {
    if (!data?.getShip?.passagePlans?.pageInfo?.hasNextPage) return;
    const nextCursor = data.getShip.passagePlans.pageInfo.endCursor;
    setCursorHistory(prev => [...prev, currentCursor]);
    setCurrentCursor(nextCursor);
    await refetch({
      id: shipId,
      first: 20,
      after: nextCursor
    });
  };

  const handlePreviousPage = async () => {
    if (cursorHistory.length === 0) return;
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory(prev => prev.slice(0, -1));
    setCurrentCursor(previousCursor);
    await refetch({
      id: shipId,
      first: 20,
      after: previousCursor
    });
  };

  return (
    <div className="ship-detail">
      <div className="ship-detail-header">
        <button
          className="back-button"
          onClick={() => navigate('/')}
        >
          ← 一覧に戻る
        </button>
        <h1>船舶詳細情報</h1>
      </div>

      <div className="ship-detail-content">
        <div className="ship-info-card">
          <div className="ship-info-header">
            <div className="ship-icon">
              {getShipTypeIcon(ship.kind)}
            </div>
            <div className="ship-info">
              <h2 className="ship-name">{ship.name || '船名不明'}</h2>
              <p className="ship-type">{ship.kind?.name || '船種不明'}</p>
            </div>
            <div className="ship-badges">
              {ship.huge && <span className="huge-flag">巨大船</span>}
            </div>
          </div>

          <div className="ship-details">
            <div className="detail-row">
              <span className="detail-label">旗国:</span>
              <span className="detail-value flag-nation">
                {ship.flagNation?.imageUrl && (
                  <img
                    src={ship.flagNation.imageUrl}
                    alt={ship.flagNation.name || '旗国'}
                    className="flag-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <span className="flag-text">
                  {ship.flagNation?.name || '不明'}
                  {ship.flagNation?.abbreviation && ` (${ship.flagNation.abbreviation})`}
                </span>
              </span>
            </div>

            {ship.length && (
              <div className="detail-row">
                <span className="detail-label">長さ:</span>
                <span className="detail-value">{ship.length}m</span>
              </div>
            )}

            {ship.tonnage && (
              <div className="detail-row">
                <span className="detail-label">トン数:</span>
                <span className="detail-value">{ship.tonnage.toLocaleString()}t</span>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">作成日時:</span>
              <span className="detail-value">{formatDate(ship.createdAt)}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">更新日時:</span>
              <span className="detail-value">{formatDate(ship.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="passage-plans-section">
          {(() => {
            const currentPage = cursorHistory.length + 1;
            const startItem = (currentPage - 1) * 20 + 1;
            const endItem = (currentPage - 1) * 20 + (ship.passagePlans?.nodes?.length || 0);
            const totalCount = !ship.passagePlans?.pageInfo?.hasNextPage && currentPage === 1
              ? ship.passagePlans?.nodes?.length || 0
              : null;

            return (
              <>
                <h3>
                  航行情報
                  {totalCount ? ` (全${totalCount}件)` : ''}
                  {`: ${startItem}-${endItem}件を表示`}
                </h3>
                {ship.passagePlans?.nodes && ship.passagePlans.nodes.length > 0 ? (
                  <>
                    <div className="passage-plans-table-container">
                      <table className="passage-plans-table">
                        <thead>
                          <tr>
                            <th>目的地</th>
                            <th>方向</th>
                            <th>入航予定</th>
                            <th>状態</th>
                            <th>フラグ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ship.passagePlans.nodes.map((passagePlan) => {
                            const statusInfo = getStatusInfo(passagePlan.status);
                            return (
                              <tr
                                key={passagePlan.id}
                                className="passage-plan-row"
                                onClick={() => handlePassagePlanClick(passagePlan.id)}
                              >
                                <td className="destination-cell">
                                  {passagePlan.destination?.name || (passagePlan.direction === 'north' ? '入港不明' : passagePlan.direction === 'south' ? '出港不明' : '入港/出港不明')}
                                </td>
                                <td className="direction-cell">
                                  {getDirectionText(passagePlan.direction)}
                                </td>
                                <td className="enter-at-cell">
                                  {formatDate(passagePlan.enterAt)}
                                </td>
                                <td className="status-cell">
                                  <span
                                    className="status-badge"
                                    style={{ backgroundColor: statusInfo.color }}
                                  >
                                    {statusInfo.icon} {statusInfo.text}
                                  </span>
                                </td>
                                <td className="flags-cell">
                                  {passagePlan.viaWaypoint && (
                                    <span className="flag via-waypoint">中ノ瀬</span>
                                  )}
                                  {passagePlan.withPilot && (
                                    <span className="flag with-pilot">水先人</span>
                                  )}
                                  {!passagePlan.viaWaypoint && !passagePlan.withPilot && (
                                    <span className="flag-empty">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="pagination">
                      <button
                        className="pagination-button"
                        onClick={handlePreviousPage}
                        disabled={cursorHistory.length === 0}
                      >
                        ← 前へ
                      </button>
                      <span className="pagination-info">
                        {ship.passagePlans.nodes.length}件表示
                      </span>
                      <button
                        className="pagination-button"
                        onClick={handleNextPage}
                        disabled={!ship.passagePlans.pageInfo?.hasNextPage}
                      >
                        次へ →
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="no-data">
                    <p>関連する航行情報がありません</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ShipDetail;
